import Heading from '@/common/shared/heading'
import { Separator } from '@/common/ui/separator'
import NavigateBackButton from '@/common/components/NavigateBackButton'
import { EventForm, FormValues as CustomFormValues } from '../EventForm'
import { TurnoverEventForm } from '../TurnoverEventForm'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { toast } from 'sonner'
import showErrorToast from '@/common/utils/showErrorToast'
import useMutationUpdateEvent from '@/modules/events/api/hooks/useMutationUpdateEvent'
import useQueryGetEventById from '@/modules/events/api/hooks/useQueryGetEventById'
import useQueryGetTurnoverEventById from '@/modules/events/api/hooks/useQueryGetTurnoverEventById'
import { useQueryClient } from '@tanstack/react-query'
import { EventsQueryKeys } from '@/modules/events/keys'
import Spinner from '@/common/components/Spinner'
import { updateTurnoverEvent } from '../../api/services'
import type { EventType, UpdateTurnoverEventRequest } from '../../type'

export default function EditEventListing() {
	const navigate = useNavigate()
	const { id } = useParams<{ id: string }>()
	const queryClient = useQueryClient()
	const [searchParams] = useSearchParams()
	const eventType = (searchParams.get('type') || 'CUSTOM') as EventType

	// Fetch custom event
	const { data: customEvent, isLoading: isLoadingCustom } =
		useQueryGetEventById(id!, eventType === 'CUSTOM')

	// Fetch turnover event
	const { data: turnoverEvent, isLoading: isLoadingTurnover } =
		useQueryGetTurnoverEventById(id!, eventType === 'TURNOVER')

	const updateEventMutation = useMutationUpdateEvent({
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: [EventsQueryKeys.getEvents],
			})
			queryClient.invalidateQueries({
				queryKey: [EventsQueryKeys.getEventById, id],
			})
			toast.success('แก้ไขกิจกรรมสำเร็จ')
			navigate('/operator/events')
		},
		onError: (error: Error) => {
			showErrorToast(error)
		},
	})

	const handleCustomEventSubmit = (values: CustomFormValues) => {
		if (!id) return
		updateEventMutation.mutate({
			id,
			payload: {
				...values,
				bannerUrl: values.bannerUrl || undefined,
				shortDescription: values.shortDescription || undefined,
				description: values.description || undefined,
				linkUrl: values.linkUrl || undefined,
			},
		})
	}

	const handleTurnoverEventSubmit = async (
		submitData: UpdateTurnoverEventRequest,
	) => {
		if (!id) return
		try {
			await updateTurnoverEvent(id, submitData)
			queryClient.invalidateQueries({
				queryKey: [EventsQueryKeys.getTurnoverEvents],
			})
			queryClient.invalidateQueries({
				queryKey: [EventsQueryKeys.getTurnoverEventById, id],
			})
			toast.success('แก้ไขกิจกรรม Turnover สำเร็จ')
			navigate('/operator/events')
		} catch (error) {
			showErrorToast(error as Error)
		}
	}

	const isLoading = eventType === 'CUSTOM' ? isLoadingCustom : isLoadingTurnover
	const event = eventType === 'CUSTOM' ? customEvent : turnoverEvent

	if (isLoading) {
		return (
			<div className="space-y-4">
				<div className="flex items-start justify-between">
					<Heading title="แก้ไขกิจกรรม" description="แก้ไขข้อมูลกิจกรรม" />
					<NavigateBackButton />
				</div>
				<Separator />
				<Spinner />
			</div>
		)
	}

	if (!event) {
		return (
			<div className="space-y-4">
				<div className="flex items-start justify-between">
					<Heading title="แก้ไขกิจกรรม" description="แก้ไขข้อมูลกิจกรรม" />
					<NavigateBackButton />
				</div>
				<Separator />
				<div className="text-center">ไม่พบกิจกรรมที่ต้องการแก้ไข</div>
			</div>
		)
	}

	const title =
		eventType === 'TURNOVER' ? 'แก้ไขกิจกรรมเทิร์นโอเวอร์' : 'แก้ไขกิจกรรม'

	return (
		<div className="space-y-4">
			<div className="flex items-start justify-between">
				<Heading title={title} description={`แก้ไขกิจกรรม: ${event.title}`} />
				<NavigateBackButton />
			</div>
			<Separator />
			{eventType === 'TURNOVER' ? (
				<TurnoverEventForm
					onSubmit={handleTurnoverEventSubmit}
					mode="edit"
					defaultValues={turnoverEvent!}
				/>
			) : (
				<EventForm
					onSubmit={handleCustomEventSubmit}
					mode="edit"
					defaultValues={{
						title: customEvent!.title,
						bannerUrl: customEvent!.bannerUrl,
						shortDescription: customEvent!.shortDescription,
						description: customEvent!.description,
						linkUrl: customEvent!.linkUrl,
					}}
				/>
			)}
		</div>
	)
}
